import * as React from 'react';
import { CheckCircle2, CircleAlert, ExternalLink, Pencil, PlugZap, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { api } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  SkeletonRow,
  useConfirm,
  useToast,
} from '@/components/ui';

interface JiraConfig {
  id: string;
  name: string;
  host: string;
  email: string;
  defaultProject: string | null;
  defaultIssueType: string | null;
  isActive: boolean;
  isDefault: boolean;
  hasApiToken: boolean;
  hasWebhookSecret: boolean;
  createdAt: string;
  updatedAt: string;
}

type FormState = {
  name: string;
  host: string;
  email: string;
  apiToken: string;
  webhookSecret: string;
  defaultProject: string;
  defaultIssueType: string;
  isActive: boolean;
  isDefault: boolean;
  clearWebhookSecret: boolean;
};

const EMPTY_FORM: FormState = {
  name: '',
  host: '',
  email: '',
  apiToken: '',
  webhookSecret: '',
  defaultProject: '',
  defaultIssueType: 'Task',
  isActive: true,
  isDefault: false,
  clearWebhookSecret: false,
};

function toForm(config: JiraConfig): FormState {
  return {
    name: config.name,
    host: config.host,
    email: config.email,
    apiToken: '',
    webhookSecret: '',
    defaultProject: config.defaultProject ?? '',
    defaultIssueType: config.defaultIssueType ?? 'Task',
    isActive: config.isActive,
    isDefault: config.isDefault,
    clearWebhookSecret: false,
  };
}

function webhookEndpoint(id: string): string {
  const apiBase = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/+$/, '');
  return `${apiBase}/jira/webhooks/${id}`;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function JiraAdminPage() {
  const { t } = useI18n();
  const { confirm } = useConfirm();
  const { toast } = useToast();
  const [configs, setConfigs] = React.useState<JiraConfig[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [editing, setEditing] = React.useState<string | 'new' | null>(null);
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState<string | null>(null);
  const [testResults, setTestResults] = React.useState<Record<string, string>>({});

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.get<JiraConfig[]>('/jira/configs');
      setConfigs(Array.isArray(result) ? result : []);
    } catch (loadError) {
      setError(errorMessage(loadError, t('adm.jira.requestFailed')));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  function startCreate() {
    setEditing('new');
    setForm(EMPTY_FORM);
  }

  function startEdit(config: JiraConfig) {
    setEditing(config.id);
    setForm(toForm(config));
  }

  function cancelEdit() {
    setEditing(null);
    setForm(EMPTY_FORM);
  }

  function setTextField(key: Exclude<keyof FormState, 'isActive' | 'isDefault' | 'clearWebhookSecret'>) {
    return (event: React.ChangeEvent<HTMLInputElement>) => setForm((current) => ({ ...current, [key]: event.target.value }));
  }

  function setBooleanField(key: 'isActive' | 'isDefault' | 'clearWebhookSecret') {
    return (event: React.ChangeEvent<HTMLInputElement>) => setForm((current) => ({ ...current, [key]: event.target.checked }));
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError('');
    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      host: form.host.trim(),
      email: form.email.trim(),
      defaultProject: form.defaultProject.trim() || undefined,
      defaultIssueType: form.defaultIssueType.trim() || undefined,
      isActive: form.isActive,
      isDefault: form.isDefault,
    };
    if (form.apiToken.trim()) payload.apiToken = form.apiToken.trim();
    if (form.clearWebhookSecret || form.webhookSecret.trim()) {
      payload.webhookSecret = form.clearWebhookSecret ? null : form.webhookSecret.trim();
    }
    try {
      if (editing === 'new') {
        await api.post('/jira/configs', payload);
      } else if (editing) {
        await api.put(`/jira/configs/${editing}`, payload);
      }
      toast({ variant: 'success', title: editing === 'new' ? t('adm.jira.added') : t('adm.jira.savedToast') });
      cancelEdit();
      await load();
    } catch (saveError) {
      setError(errorMessage(saveError, t('adm.jira.requestFailed')));
    } finally {
      setSaving(false);
    }
  }

  async function test(config: JiraConfig) {
    setTesting(config.id);
    setError('');
    try {
      const result = await api.post<{ ok: boolean; displayName: string | null }>(`/jira/configs/${config.id}/test`);
      const identity = result.displayName ? t('adm.jira.connectedAs', { name: result.displayName }) : t('adm.jira.accepted');
      setTestResults((current) => ({ ...current, [config.id]: identity }));
      toast({ variant: 'success', title: t('adm.jira.verified'), description: identity });
    } catch (testError) {
      setError(errorMessage(testError, t('adm.jira.requestFailed')));
      setTestResults((current) => ({ ...current, [config.id]: t('adm.jira.testFailed') }));
    } finally {
      setTesting(null);
    }
  }

  async function remove(config: JiraConfig) {
    if (!await confirm({
      title: t('adm.jira.confirmTitle', { name: config.name }),
      description: t('adm.jira.confirmDesc'),
      confirmLabel: t('adm.jira.confirmLabel'),
    })) return;
    try {
      await api.delete(`/jira/configs/${config.id}`);
      setConfigs((current) => current.filter((item) => item.id !== config.id));
      if (editing === config.id) cancelEdit();
    } catch (removeError) {
      setError(errorMessage(removeError, t('adm.jira.requestFailed')));
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        className="mb-0"
        breadcrumb={[{ label: t('nav.settings'), to: '/settings' }, { label: t('adm.jira.title') }]}
        title={t('adm.jira.title')}
        description={t('adm.jira.description')}
        actions={(
          <>
            <Button variant="outline" onClick={() => void load()}>{t('adm.refresh')}</Button>
            <Button onClick={startCreate}><Plus aria-hidden="true" /> {t('adm.jira.addConnection')}</Button>
          </>
        )}
      />

      <Card className="overflow-hidden border-brand-200">
        <div className="grid gap-0 md:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="border-l-4 border-brand-600 p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-brand-50 p-2 text-brand-700"><PlugZap className="h-5 w-5" aria-hidden="true" /></div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-700">{t('adm.jira.connectionDesk')}</p>
                <h2 className="mt-1 text-lg font-semibold text-ink">{t('adm.jira.heading')}</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-ink-muted">{t('adm.jira.intro')}</p>
              </div>
            </div>
          </div>
          <div className="border-t border-line bg-surface-subtle p-5 md:border-l md:border-t-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">{t('adm.jira.whatSyncs')}</p>
            <ul className="mt-3 space-y-2 text-xs text-ink-muted">
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-success-600" aria-hidden="true" /> {t('adm.jira.syncSnapshot')}</li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-success-600" aria-hidden="true" /> {t('adm.jira.syncToJira')}</li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-success-600" aria-hidden="true" /> {t('adm.jira.syncFromJira')}</li>
            </ul>
          </div>
        </div>
      </Card>

      {error && <ErrorState message={error} onRetry={() => void load()} />}

      {editing !== null && (
        <Card>
          <CardHeader><CardTitle>{editing === 'new' ? t('adm.jira.addTitle') : t('adm.jira.editTitle')}</CardTitle></CardHeader>
          <CardContent>
            <form className="grid gap-4 md:grid-cols-2" onSubmit={save}>
              <Input label={t('adm.jira.connName')} value={form.name} onChange={setTextField('name')} placeholder={t('adm.jira.connNamePh')} required />
              <Input label={t('adm.jira.siteUrl')} type="url" value={form.host} onChange={setTextField('host')} placeholder="https://your-company.atlassian.net" required hint={t('adm.jira.siteUrlHint')} />
              <Input label={t('adm.jira.accountEmail')} type="email" value={form.email} onChange={setTextField('email')} placeholder="jira-bot@example.com" required />
              <Input label={t('adm.jira.apiToken')} type="password" value={form.apiToken} onChange={setTextField('apiToken')} autoComplete="new-password" placeholder={editing === 'new' ? t('adm.jira.pasteToken') : t('adm.jira.unchanged')} required={editing === 'new'} hint={editing === 'new' ? t('adm.jira.tokenHintNew') : t('adm.jira.tokenHintEdit')} />
              <Input label={t('adm.jira.webhookSecret')} type="password" value={form.webhookSecret} onChange={setTextField('webhookSecret')} autoComplete="new-password" placeholder={editing === 'new' ? t('adm.jira.secretOptional') : t('adm.jira.unchanged')} hint={t('adm.jira.secretHint')} />
              <Input label={t('adm.jira.defaultProjectKey')} value={form.defaultProject} onChange={setTextField('defaultProject')} placeholder="SUP" autoCapitalize="characters" />
              <Input label={t('adm.jira.defaultIssueType')} value={form.defaultIssueType} onChange={setTextField('defaultIssueType')} placeholder="Task" />
              <div className="flex flex-wrap items-center gap-5 md:col-span-2">
                <label className="flex items-center gap-2 text-sm text-ink-muted"><input type="checkbox" checked={form.isActive} onChange={setBooleanField('isActive')} className="h-4 w-4 rounded border-line-strong" /> {t('adm.jira.activeConn')}</label>
                <label className="flex items-center gap-2 text-sm text-ink-muted"><input type="checkbox" checked={form.isDefault} onChange={setBooleanField('isDefault')} className="h-4 w-4 rounded border-line-strong" /> {t('adm.jira.defaultConn')}</label>
                {editing !== 'new' && <label className="flex items-center gap-2 text-sm text-ink-muted"><input type="checkbox" checked={form.clearWebhookSecret} onChange={setBooleanField('clearWebhookSecret')} className="h-4 w-4 rounded border-line-strong" /> {t('adm.jira.clearSecret')}</label>}
              </div>
              <div className="flex gap-2 pt-2 md:col-span-2">
                <Button type="submit" loading={saving}>{editing === 'new' ? t('adm.jira.addConnection') : t('adm.saveChanges')}</Button>
                <Button type="button" variant="outline" disabled={saving} onClick={cancelEdit}>{t('common.cancel')}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <Card><CardContent className="p-0"><SkeletonRow rows={3} /></CardContent></Card>
      ) : configs.length === 0 ? (
        <Card><CardContent className="p-0"><EmptyState title={t('adm.jira.noConns')} description={t('adm.jira.noConnsDesc')} action={<Button onClick={startCreate}><Plus aria-hidden="true" /> {t('adm.jira.addConnection')}</Button>} /></CardContent></Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {configs.map((config) => (
            <Card key={config.id} className="overflow-hidden">
              <CardHeader className="border-b border-line bg-surface-subtle pb-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <CardTitle className="truncate">{config.name}</CardTitle>
                      {config.isDefault && <Badge variant="info">{t('adm.jira.default')}</Badge>}
                      <Badge variant={config.isActive ? 'success' : 'neutral'}>{config.isActive ? t('adm.active') : t('adm.inactive')}</Badge>
                    </div>
                    <p className="mt-2 truncate text-sm text-ink-muted">{config.host}</p>
                  </div>
                  {config.hasApiToken ? <ShieldCheck className="h-5 w-5 shrink-0 text-success-600" aria-label={t('adm.jira.tokenStored')} /> : <CircleAlert className="h-5 w-5 shrink-0 text-warning-600" aria-label={t('adm.jira.tokenMissing')} />}
                </div>
              </CardHeader>
              <CardContent className="space-y-4 pt-5">
                <div className="grid gap-3 text-sm sm:grid-cols-2">
                  <div><p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">{t('adm.jira.accountEmailLabel')}</p><p className="mt-1 truncate text-ink-muted">{config.email}</p></div>
                  <div><p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">{t('adm.jira.defaults')}</p><p className="mt-1 text-ink-muted">{config.defaultProject || t('adm.jira.noProject')} · {config.defaultIssueType || 'Task'}</p></div>
                </div>
                <div className="rounded-md border border-line bg-surface-subtle p-3">
                  <p className="text-xs font-medium text-ink-subtle">{t('adm.jira.webhookEndpoint')}</p>
                  <code className="mt-1 block break-all text-xs text-ink-muted">{webhookEndpoint(config.id)}</code>
                  <p className="mt-2 text-xs text-ink-subtle">{t('adm.jira.secretConfigured', { value: config.hasWebhookSecret ? t('adm.jira.yes') : t('adm.jira.no') })}</p>
                </div>
                {testResults[config.id] && <p className="rounded-md bg-success-50 px-3 py-2 text-xs text-success-700">{testResults[config.id]}</p>}
                <div className="flex flex-wrap justify-end gap-2">
                  <Button size="sm" variant="outline" loading={testing === config.id} disabled={!config.isActive} onClick={() => void test(config)}><PlugZap aria-hidden="true" /> {t('adm.jira.testConnection')}</Button>
                  <Button size="sm" variant="outline" onClick={() => startEdit(config)}><Pencil aria-hidden="true" /> {t('adm.edit')}</Button>
                  <Button size="sm" variant="ghost" onClick={() => void remove(config)}><Trash2 aria-hidden="true" /> {t('common.delete')}</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <p className="flex items-start gap-2 text-xs text-ink-subtle"><ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />{t('adm.jira.footer')}</p>
    </div>
  );
}
